import React, { createContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase/supabaseClient';

// Criação do contexto
export const TeamContext = createContext();

export const TeamProvider = ({ children }) => {
  // Estados para dados da equipe
  const [teamMembers, setTeamMembers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [totalSalaries, setTotalSalaries] = useState(0);
  const [loading, setLoading] = useState(true);
  
  // Carregar dados do Supabase
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Buscar membros da equipe
        const { data: membersData, error: membersError } = await supabase
          .from('team_members')
          .select('*')
          .order('name');
        
        if (membersError) {
          console.error('Erro ao buscar membros da equipe:', membersError);
        }
        
        if (membersData) {
          setTeamMembers(membersData);
          
          // Buscar pagamentos
          const { data: paymentsData, error: paymentsError } = await supabase
            .from('team_payments')
            .select('*')
            .order('year', { ascending: false })
            .order('month', { ascending: false });
          
          if (paymentsError) {
            console.error('Erro ao buscar pagamentos:', paymentsError);
          }
          
          if (paymentsData) {
            setPayments(paymentsData);
          }
        }
      } catch (error) {
        console.error('Erro ao buscar dados da equipe:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
    
    // Configurar listeners para mudanças
    const membersSubscription = supabase
      .channel('public:team_members')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'team_members' }, fetchData)
      .subscribe();
    
    const paymentsSubscription = supabase
      .channel('public:team_payments')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'team_payments' }, fetchData)
      .subscribe();
    
    // Limpar listeners ao desmontar
    return () => {
      supabase.removeChannel(membersSubscription);
      supabase.removeChannel(paymentsSubscription);
    };
  }, []);

  // Calcular total de salários sempre que a lista de membros mudar
  useEffect(() => {
    const total = teamMembers.reduce((sum, member) => sum + parseFloat(member.salary || 0), 0);
    setTotalSalaries(total);
  }, [teamMembers]);

  // Adicionar novo membro à equipe
  const addTeamMember = async (newMember) => {
    try {
      const { data, error } = await supabase
        .from('team_members')
        .insert({
          name: newMember.name,
          start_date: newMember.startDate,
          salary: parseFloat(newMember.salary),
          payment_day: parseInt(newMember.paymentDay),
          bonus_percentage: parseFloat(newMember.bonusPercentage || 0),
          pix_key: newMember.pixKey || '',
          created_at: new Date().toISOString()
        })
        .select();
      
      if (error) {
        console.error('Erro ao adicionar membro da equipe:', error);
        return null;
      }
      
      return data[0];
    } catch (error) {
      console.error('Erro ao adicionar membro da equipe:', error);
      return null;
    }
  };

  // Remover membro da equipe
  const removeTeamMember = async (id) => {
    try {
      // Primeiro remover pagamentos associados
      const { error: paymentsError } = await supabase
        .from('team_payments')
        .delete()
        .eq('member_id', id);
      
      if (paymentsError) {
        console.error('Erro ao remover pagamentos do membro:', paymentsError);
        return false;
      }
      
      // Depois remover o membro
      const { error } = await supabase
        .from('team_members')
        .delete()
        .eq('id', id);
      
      if (error) {
        console.error('Erro ao remover membro da equipe:', error);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Erro ao remover membro da equipe:', error);
      return false;
    }
  };

  // Atualizar dados de um membro
  const updateTeamMember = async (id, updatedData) => {
    try {
      const { error } = await supabase
        .from('team_members')
        .update({
          name: updatedData.name,
          start_date: updatedData.startDate,
          salary: parseFloat(updatedData.salary),
          payment_day: parseInt(updatedData.paymentDay),
          bonus_percentage: parseFloat(updatedData.bonusPercentage || 0),
          pix_key: updatedData.pixKey || '',
          updated_at: new Date().toISOString()
        })
        .eq('id', id);
      
      if (error) {
        console.error('Erro ao atualizar membro da equipe:', error);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Erro ao atualizar membro da equipe:', error);
      return false;
    }
  };

  // Registrar pagamento para um mês
  const registerPayment = async (memberId, month, year, status) => {
    try {
      // Verificar se já existe um pagamento para este membro/mês/ano
      const { data: existingPayment, error: checkError } = await supabase
        .from('team_payments')
        .select('id')
        .eq('member_id', memberId)
        .eq('month', month)
        .eq('year', year)
        .maybeSingle();
      
      if (checkError) {
        console.error('Erro ao verificar pagamento existente:', checkError);
        return false;
      }
      
      if (existingPayment) {
        // Atualizar pagamento existente
        const { error: updateError } = await supabase
          .from('team_payments')
          .update({
            status: status,
            payment_date: status === 'paid' ? new Date().toISOString() : null,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingPayment.id);
        
        if (updateError) {
          console.error('Erro ao atualizar pagamento:', updateError);
          return false;
        }
      } else {
        // Criar novo pagamento
        const { error: insertError } = await supabase
          .from('team_payments')
          .insert({
            member_id: memberId,
            month: month,
            year: year,
            status: status,
            payment_date: status === 'paid' ? new Date().toISOString() : null,
            created_at: new Date().toISOString()
          });
        
        if (insertError) {
          console.error('Erro ao criar pagamento:', insertError);
          return false;
        }
      }
      
      return true;
    } catch (error) {
      console.error('Erro ao registrar pagamento:', error);
      return false;
    }
  };

  // Verificar status de pagamento
  const getPaymentStatus = (memberId, month, year) => {
    const payment = payments.find(p => 
      p.member_id === memberId && 
      p.month === month && 
      p.year === year
    );
    
    return payment ? payment.status : 'pending';
  };

  return (
    <TeamContext.Provider
      value={{
        teamMembers,
        payments,
        totalSalaries,
        loading,
        addTeamMember,
        removeTeamMember,
        updateTeamMember,
        registerPayment,
        getPaymentStatus
      }}
    >
      {children}
    </TeamContext.Provider>
  );
};